import debug from 'debug';
import * as _ from 'lodash';
import { saveAs } from 'file-saver';
import {
  SlickGrid,
  SlickPlugin,
  Column,
  SlickGridModel,
  SlickDataView,
  SlickEvent,
  ColumnSort,
  Formatter,
  FormatterResultWithHtml,
  FormatterResultWithText,
  FormatterEx,
} from 'slickgrid';
import { he } from 'date-fns/locale';
import { head } from 'lodash';

const log = debug('itp:slick:helpers');

export function assertNonNullish<TValue>(
  value: TValue,
  message: string,
): asserts value is NonNullable<TValue> {
  if (value === null || value === undefined) {
    throw Error(message);
  }
}

export function cssImage(value) {
  value = value.toLowerCase().replace('_', '-').replace('.', '-');

  return `slick--img-${value}`;
}

export function isSystemColumn<T>(col: Column<T>) {
  return col.name === '' || col.systemColumn;
}

export function isExportableColumn<T>(col: Column<T>) {
  return !isSystemColumn(col) && !col.noExport;
}

export function getSortColumns<T>(grid: SlickGridModel<T>) {
  const columns = grid.getColumns();

  return _.map(grid.getSortColumns(), (col) => ({
    sortCol: columns[grid.getColumnIndex(col.columnId)],
    sortAsc: col.sortAsc,
  }));
}

const isValue = (o) => o != null && o.toString().trim().length > 0;

function toComparableValue(value: any): any {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => (item != null && typeof item === 'object' ? JSON.stringify(item) : item))
      .join(',');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * dataview default sorter algorithm - it sorts by the provided cols
 */
export function sortDataView<T>(
  grid: SlickGrid,
  dataView: SlickDataView<T>,
  sorters: ColumnSort[],
) {
  dataView.sort(function (row1, row2) {
    let s, col;

    for (let i = 0, len = sorters.length; i < len; i++) {
      s = sorters[i];
      col = s.sortCol;

      if (!s || !col || !col.sortable) {
        continue;
      }

      const field = (col.params && col.params.sortPath) || col.field,
        sign = s.sortAsc ? 1 : -1,
        value1 = col.textFormatter
          ? col.getFormattedText(row1, null, _.get(row1, field), col, row1)
          : _.get(row1, field),
        value2 = col.textFormatter
          ? col.getFormattedText(row2, null, _.get(row2, field), col, row2)
          : _.get(row2, field);

      if (!isValue(value1) && !isValue(value2)) {
        return 0;
      }
      if (!isValue(value1) && isValue(value2)) {
        return -1 * sign;
      }
      if (!isValue(value2) && isValue(value1)) {
        return 1 * sign;
      }

      const v1 = toComparableValue(value1);
      const v2 = toComparableValue(value2);
      const result = (v1 === v2 ? 0 : v1 > v2 ? 1 : -1) * sign;

      if (result !== 0) {
        return result;
      }
    }

    return 0;
  }, true);

  grid.invalidateAllRows();
  grid.render();
}

function getCellValueForExport<T>(
  grid: SlickGrid,
  column: Column<T>,
  data: T,
  options: SaveDataOptions,
) {
  const fmt = column.formatter as FormatterEx;
  const value = data[_.toString(column.field)];

  let numberFormatter;

  if (options.localeCode) {
    numberFormatter = Intl.NumberFormat(options.localeCode);
  }

  function toResult(v: any) {
    if (_.isNumber(v) && numberFormatter) {
      return numberFormatter.format(v).replace(/\s/g, '');
    }
    return v;
  }

  if (_.isFunction(fmt)) {
    if ((fmt as FormatterEx).exportRawData) {
      return toResult(value);
    }

    if (_.isFunction(fmt.exportDataFormatter)) {
      return toResult(
        fmt.exportDataFormatter(-1, -1, value, column, data, grid),
      );
    }

    return toResult(fmt(-1, -1, value, column, data, grid));
  }

  return toResult(value);
}

function normCellValue(value, quoteChar) {
  if (isEmpty(value)) {
    return '';
  }

  if (_.isArray(value)) {
    if (value.length === 0) {
      return '';
    }

    value = value.join(', ');
  }

  return `${quoteChar}${value}${quoteChar}`;
}

function formatDataItems<T>(
  grid: SlickGrid,
  columns: Column<T>[],
  items: T[],
  options: SaveDataOptions,
) {
  const fields: string[] = [];
  const columnsByField: Dictionary<Column> = {};
  const header: string[] = [];

  columns.forEach((col) => {
    const fieldName = _.toString(col.id);
    fields.push(fieldName);
    columnsByField[fieldName] = col;
    header.push(_.toString(col.name));
  });

  const result = [header];

  let row;
  for (let i = 0, len = items.length; i < len; i++) {
    row = items[i];

    if (row.__nonDataRow) {
      continue;
    }

    result.push(
      _.map(fields, (f) =>
        getCellValueForExport(grid, columnsByField[f], row, options),
      ),
    );
  }

  return result;
}

const BOM = '\uFEFF';

export interface SaveDataOptions {
  delimiter: string;
  quoteChar: string;
  localeCode: string;
}

export async function saveData<T>(
  grid: SlickGrid,
  items: T[],
  fileName: string,
  format: string = 'csv',
  options: SaveDataOptions,
) {
  log('saveData - options: %o', options);

  const columns = _.filter(grid.getColumns(), isExportableColumn);
  log('saveData - columns: %o', options);

  const rows = formatDataItems(grid, columns, items, options);

  if (rows.length < 2) {
    // no data, only the column
    return;
  }

  const delimiter = options.delimiter,
    quoteChar = options.quoteChar,
    content =
      BOM +
      rows
        .map((row) =>
          row.map((p) => normCellValue(p, quoteChar)).join(delimiter),
        )
        .join('\n');

  saveAs(new Blob([content], { type: 'text/plain;charset=utf-8' }), fileName);
}

export function fixColumnHeaderClickedAfterResize<T>(grid: SlickGrid) {
  let columnResized;

  grid.onColumnsResized.subscribe(() => (columnResized = new Date().getTime()));

  grid.onHeaderClick.subscribe((e) => {
    if (columnResized && new Date().getTime() - columnResized < 1000) {
      e.stopImmediatePropagation();
      (e as unknown as Event).preventDefault();
    }
  });
}

const SI_UNITS = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
const OT_UNITS = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

export function humanFileSize(bytes, si) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si ? SI_UNITS : OT_UNITS;

  let u = -1;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);

  return bytes.toFixed(1) + ' ' + units[u];
}

export function containsFilterFn(grid): Function {
  return function (item) {
    let columns = grid.getColumns(),
      result = true;

    for (let i = 0; i < columns.length; i++) {
      const col = columns[i],
        filterValues = col && col.filterValues;

      if (col && filterValues && filterValues.length > 0) {
        if (_.isArray(item[col.field])) {
          result =
            result &&
            _.some(item[col.field], (p) => _.indexOf(filterValues, p) > -1);
        } else {
          result = result && _.indexOf(filterValues, item[col.field]) > -1;
        }
      }
    }
    return result;
  };
}

export function chainingFilters(...filters) {
  return function (item) {
    return _.every(filters, (fn) => fn(item));
  };
}

export function containsValue(cellValue, filterValue) {
  if (_.isArray(cellValue)) {
    cellValue = cellValue.join(', ');
  }

  if (isEmpty(cellValue)) {
    return false;
  }

  if (!_.isString(cellValue)) {
    cellValue = cellValue.toString();
  }

  return cellValue.toLowerCase().indexOf(filterValue.toLowerCase()) > -1;
}

export function isPluginObject<T>(obj: object): obj is SlickPlugin {
  return _.isFunction(obj['init']) && _.isFunction(obj['destroy']);
}

export function isSlickEvent<T>(obj: object): obj is SlickEvent<T> {
  return (
    _.isFunction(obj['subscribe']) &&
    _.isFunction(obj['unsubscribe']) &&
    _.isFunction(obj['notify'])
  );
}

export function isEmpty(value: any) {
  return (
    _.isUndefined(value) ||
    value === null ||
    (_.isString(value) && value.trim() === '')
  );
}

export function createElementText(
  tagName: string,
  attributes?: object,
  content: string | null = null,
) {
  let result = `<${tagName}`;

  _.each(attributes, (v, k) => {
    if (v !== null) {
      result += ` ${k}="${decodeURIComponent(v)}"`;
    }
  });

  if (content === null) {
    content = '';
  }

  return `${result}>${content}</${tagName}>`;
}

export function applyFormatterResult(
  el: HTMLElement,
  result:
    | string
    | HTMLElement
    | DocumentFragment
    | FormatterResultWithHtml
    | FormatterResultWithText,
) {
  if (_.isString(result)) {
    el.textContent = result;
    return;
  }

  el.textContent = _.toString(result);
}

export function getCssClasses(value?: any) {
  if (!value) {
    return;
  }

  if (_.isArray(value)) {
    return _.map(value, getCssClasses).join(' ');
  }

  if (_.isObject(value)) {
    return _.reduce(
      value,
      (m, v, k) => {
        if (v) {
          return m + ' ' + k;
        }
        return m;
      },
      '',
    );
  }

  return value.toString();
}

export interface DataViewLike<T = any> {
  getLength(): number;
  getItem(index: number): T | undefined;
  getItemById?(id: any): T | undefined;
}

export function applyUserVisibleSelectionDiff(
  newVisibleIds: any[],
  oldVisibleIds: any[],
  fullSelectedIds: any[],
): any[] {
  const newSet = new Set(newVisibleIds);
  const oldSet = new Set(oldVisibleIds);
  const fullSet = new Set(fullSelectedIds);

  for (const id of newVisibleIds) {
    if (!oldSet.has(id)) {
      fullSet.add(id);
    }
  }
  for (const id of oldVisibleIds) {
    if (!newSet.has(id)) {
      fullSet.delete(id);
    }
  }

  return Array.from(fullSet);
}

export function recomputeVisibleSelectionFromDataView(
  dataView: DataViewLike | null,
  fullSelectedIds: any[],
): { indexes: number[]; ids: any[] } {
  const indexes: number[] = [];
  const ids: any[] = [];

  if (!dataView || typeof dataView.getLength !== 'function') {
    return { indexes, ids };
  }
  if (fullSelectedIds.length === 0) {
    return { indexes, ids };
  }

  const fullSet = new Set(fullSelectedIds);
  const len = dataView.getLength();
  for (let i = 0; i < len; i++) {
    const item = dataView.getItem(i);
    if (
      item != null &&
      (item as any).id != null &&
      fullSet.has((item as any).id)
    ) {
      indexes.push(i);
      ids.push((item as any).id);
    }
  }

  return { indexes, ids };
}

export function pruneMissingIds(
  fullSelectedIds: any[],
  allItems: ReadonlyArray<any>,
): any[] {
  if (fullSelectedIds.length === 0) {
    return fullSelectedIds;
  }
  const validIdSet = new Set<any>();
  for (const item of allItems) {
    if (item != null && item.id != null) {
      validIdSet.add(item.id);
    }
  }
  return fullSelectedIds.filter((id) => validIdSet.has(id));
}
